'use client';

import { FunctionComponent, ReactNode, useCallback, useEffect, useState } from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';

import { logAnalyticsEvent, noopFunction } from '@vestico/utils';

const isProduction = process.env.NODE_ENV === 'production';

export interface ExceptionReporterProps {
  provider?: string;
  children: ReactNode | ReactNode[];
}

const ErrorFallback: FunctionComponent<FallbackProps> = ({ resetErrorBoundary }) => {
  useEffect(() => {
    resetErrorBoundary();
  }, [resetErrorBoundary]);

  return null;
};

export const ExceptionReporter = ({ provider, children }: ExceptionReporterProps) => {
  const [didAddRejectionListener, setDidAddRejectionListener] = useState(false);
  const [didAddErrorListener, setDidAddErrorListener] = useState(false);

  const handleRejection = useCallback(
    (e: PromiseRejectionEvent) => {
      const { reason } = e;
      console.error('Unhandled rejection');
      try {
        const { message } = reason;
        const errorString = !message || typeof message === 'string' ? message : JSON.stringify(reason);
        if (isProduction) {
          logAnalyticsEvent({
            category: 'exception',
            event: 'unhandled rejection',
            name: errorString,
            dimensions: { provider },
          });
        }
      } catch (error) {
        console.error(`Unhandled rejection c: ${error}`);
        console.error(error);
        if (isProduction) {
          logAnalyticsEvent({
            category: 'exception',
            event: 'unhandled rejection-catch',
            name: reason,
            dimensions: { provider },
          });
        }
      }

      return false;
    },
    [provider]
  );

  const handleError = useCallback(
    (e: ErrorEvent) => {
      try {
        const { error } = e;
        const { message } = error ?? { message: undefined };
        const errorString = !message || typeof message === 'string' ? message : JSON.stringify(error);
        if (isProduction) {
          logAnalyticsEvent({
            category: 'exception',
            event: 'unhandled error',
            name: errorString,
            dimensions: { provider },
          });
        }
      } catch (error) {
        console.error(`Unhandled error c: ${error}`);
        console.error(error);
        if (isProduction) {
          logAnalyticsEvent({
            category: 'exception',
            event: 'unhandled error-catch',
            name: error,
            dimensions: { provider },
          });
        }
      }

      return false;
    },
    [provider]
  );

  useEffect(() => {
    if (didAddRejectionListener || typeof window === 'undefined') {
      return noopFunction;
    }

    setDidAddRejectionListener(true);
    window.addEventListener('unhandledrejection', handleRejection);
    return () => window.removeEventListener('unhandledrejection', handleRejection);
  }, [didAddRejectionListener, handleRejection]);

  useEffect(() => {
    if (didAddErrorListener || typeof window === 'undefined') {
      return noopFunction;
    }

    setDidAddErrorListener(true);
    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, [didAddErrorListener, handleError]);

  const onRenderError = useCallback(
    (error: Error) => {
      console.error(`Error boundary: ${error}`);
      console.error(error.stack);
      if (isProduction) {
        logAnalyticsEvent({
          category: 'exception',
          event: 'error-boundary',
          name: error.message,
          dimensions: { provider },
        });
      }
    },
    [provider]
  );

  const [someKey, setSomeKey] = useState<string | null>(null);

  const onResetError = useCallback(() => {
    setSomeKey(null);
  }, []);

  return (
    <ErrorBoundary onError={onRenderError} FallbackComponent={ErrorFallback} onReset={onResetError} resetKeys={[someKey]}>
      {children}
    </ErrorBoundary>
  );
};
